import { Section, Overline, QuoteBanner, Card } from "../components/Shared";
import { PROCESS } from "../lib/data";
import { IMAGES } from "../lib/assets";
import { FileCheck2, Boxes, Cloud, MapPinned } from "lucide-react";
import { Seo } from "../components/Seo";
import { FaqSection } from "../components/FaqSection";
import { FAQ_PROCESS } from "../lib/faq";
import { howToSchema, faqSchema } from "../lib/seo";

const SCHEMA = [howToSchema(), faqSchema(FAQ_PROCESS)];
const CRUMBS = [["How It Works", "/how-it-works"]];

const DELIVERY_FORMATS = [
  { icon: MapPinned, label: "GIS layers (GeoTIFF, Shapefile)" },
  { icon: FileCheck2, label: "Large-format printable maps" },
  { icon: Cloud, label: "Cloud-accessible interactive maps" },
  { icon: Boxes, label: "Summary report with key metrics" },
];

export default function HowItWorks() {
  return (
    <>
      <Seo page="howItWorks" jsonLd={SCHEMA} breadcrumbs={CRUMBS} />
      <Section className="pt-36">
        <div className="max-w-3xl">
          <Overline>How It Works</Overline>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
            A simple, full-service process
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-700 leading-relaxed">
            You never have to touch a sonar unit or learn new software. From the first conversation to the
            final delivered map, our team handles the entire process for you.
          </p>
        </div>
      </Section>

      <Section className="pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {PROCESS.map((p) => (
            <Card key={p.step} testId={`process-step-${p.step}`} className="relative p-8 overflow-hidden">
              <div className="absolute -top-6 -right-4 font-mono text-8xl font-bold text-cyan-600/10 select-none">
                {p.step}
              </div>
              <div className="relative">
                <div className="font-mono text-sm font-semibold text-cyan-700">STEP {p.step}</div>
                <h3 className="text-2xl font-bold text-slate-900 mt-2">{p.title}</h3>
                <p className="text-slate-700 mt-3 leading-relaxed">{p.desc}</p>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
            <img src={IMAGES.gisDeliverable} alt="Finished GIS map deliverable" className="w-full object-cover aspect-[4/3]" />
          </div>
          <div>
            <Overline>What You Receive</Overline>
            <h2 className="mt-4 text-3xl font-bold text-slate-900 tracking-tight">Decision-ready deliverables</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Every survey is delivered in the formats your team actually uses — from GIS-ready files for your
              engineers to beautiful printed maps for your board and residents.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {DELIVERY_FORMATS.map((d) => (
                <div key={d.label} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <d.icon className="w-5 h-5 text-cyan-700 shrink-0" />
                  <span className="text-base text-slate-800">{d.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <FaqSection items={FAQ_PROCESS} title="Questions about the process" />

      <QuoteBanner />
    </>
  );
}
